import React from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faPersonArrowDownToLine } from '@fortawesome/sharp-solid-svg-icons/faPersonArrowDownToLine';
import { faTablePicnic } from '@fortawesome/sharp-solid-svg-icons/faTablePicnic';
import { faPeople } from '@fortawesome/sharp-solid-svg-icons/faPeople';

import { faStore } from '@fortawesome/pro-solid-svg-icons/faStore';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';
import { faStairs } from '@fortawesome/pro-solid-svg-icons/faStairs';
import { faPersonSeat } from '@fortawesome/pro-solid-svg-icons/faPersonSeat';

import { CubeTransparentIcon, ShoppingCartIcon } from '@heroicons/react/20/solid';

import { spaceTypeIconMap } from '../../../helpers/fontAwesome.js'
import { t } from '../../../helpers/t';
import { Tooltip, useTooltip } from '../../Tooltip';

const Item = ({ label, children, value }) => {
  label = label ? label.replace(':', '') : null;
  const tooltip = label ? useTooltip() : null;

  return (
    <div
      {...(tooltip ? { ref: tooltip.refs.setReference } : {})}
      {...(tooltip ? tooltip.getReferenceProps() : {})}
      className="flex-1 min-w-[120px] max-w-[140px] p-3 border-r border-stone-200 border-solid last:border-r-0 hover:bg-stone-100 transition-colors sm:min-w-[100px] sm:max-w-[120px]"
      itemProp="additionalProperty"
      itemType="http://schema.org/PropertyValue"
      itemScope
    >
      <div className="flex flex-col items-center text-center h-full">
        <div className="mb-2 h-7 flex items-center justify-center">{children}</div>
        <div className="text-xs font-normal text-stone-900 text-balance leading-tight">
          <div itemProp="value">{value}</div>
          <meta itemProp="name" content={label} />
        </div>
      </div>
      {
        tooltip && (
          <Tooltip {...tooltip}>
            {label}
          </Tooltip>
        )
      }
    </div>
  );
};

function SpaceTypeIcon({ value: faIconName }) {
  if (faIconName) {
    return <FontAwesomeIcon icon={spaceTypeIconMap[faIconName]} className="text-[24px]" aria-hidden />
  }

  return <FontAwesomeIcon icon={faStore} className="text-[24px]" aria-hidden />;
}

function LocationTypeIcon({ value }) {
  switch (value) {
    case 'Entire':
      return <FontAwesomeIcon icon={faCircle} className="text-[24px]" aria-hidden />

    default:
      return (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 512 512">
          <path d="M448 256c0-106-86-192-192-192V448c106 0 192-86 192-192zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256z" />
        </svg>
      )
  }
}

function ListingPositionIcon({ value }) {
  switch (value.toLowerCase()) {
    case 'upstairs':
      return (
        <svg className="w-6 h-6 align-middle" fill="currentColor" viewBox="0 0 24 24">
          <path d="m8.5,0C9.881,0,11,1.119,11,2.5s-1.119,2.5-2.5,2.5-2.5-1.119-2.5-2.5S7.119,0,8.5,0Zm.544,22.58l11.322-9.58h.134c1.93,0,3.5-1.57,3.5-3.5s-1.57-3.5-3.5-3.5h-1.669c-1.417,0-2.793.504-3.875,1.42L3.634,17h-.134c-1.93,0-3.5,1.57-3.5,3.5s1.57,3.5,3.5,3.5h1.669c1.417,0,2.793-.504,3.875-1.42Zm2.455-14.855c-.611-1.027-1.72-1.725-2.999-1.725-1.93,0-3.5,1.57-3.5,3.5v3.725l6.499-5.5Z" />
        </svg>
      );

    case 'shopping_mall':
      return <ShoppingCartIcon width={25} />;

    case 'ground_floor_street':
    case 'ground_floor_backyard':
      return <FontAwesomeIcon icon={faPersonArrowDownToLine} className="text-[24px]" aria-hidden />;

    case 'terrace':
      return <FontAwesomeIcon icon={faTablePicnic} className="text-[24px]" aria-hidden />;

    default:
      return <FontAwesomeIcon icon={faStairs} className="text-[24px]" aria-hidden />;
  }
}

export function DescriptionIcons() {
  const { space_type, location_type, standing_capacity, seating_capacity, readable_native_size, position } = useSelector((state) => state.listing);

  return (
    <div id="quick_descriptions" className="flex flex-wrap gap-3 mb-2 text-stone-700 justify-center">
      <Item label={t('listings.show.sections.information.space_type')} value={space_type.name}>
        <SpaceTypeIcon value={space_type.icon} />
      </Item>

      <Item label={t('listings.show.sections.information.rental_capacity')} value={location_type.name}>
        <LocationTypeIcon value={location_type.name} />
      </Item>

      <Item label={t('listings.show.sections.information.size')} value={readable_native_size}>
        <CubeTransparentIcon width={25} />
      </Item>

      {standing_capacity && (
        <Item
          label={t('listings.show.sections.information.standing_capacity.label')}
          value={t('listings.show.sections.information.standing_capacity.value', { value: standing_capacity })}
        >
          <FontAwesomeIcon icon={faPeople} className="text-[24px]" aria-hidden />
        </Item>
      )}

      {seating_capacity && (
        <Item
          label={t('listings.show.sections.information.seating_capacity.label')}
          value={t('listings.show.sections.information.seating_capacity.value', { value: seating_capacity })}
        >
          <FontAwesomeIcon icon={faPersonSeat} className="text-[24px]" aria-hidden />
        </Item>
      )}

      {
        position && (
          <Item label={t('listings.show.sections.information.position.title')} value={t(`listings.show.sections.information.position.${position.toLowerCase()}`)}>
            <ListingPositionIcon value={position} />
          </Item>
        )
      }
    </div>
  );
}
